import {
  type CSSProperties,
  type ReactNode,
  memo,
  useEffect,
  useState,
} from "react";
import { IconFa } from "@cruk/cruk-react-components";
import { faSnowflake } from "@fortawesome/free-solid-svg-icons";
import { faCanadianMapleLeaf } from "@fortawesome/free-brands-svg-icons";

import styles from "@fwa/src/components/Seasonal/styles.module.css";

const batGif = "/assets/images/img/bat.gif";

const getRandomInt = (min: number, max: number): number => {
  const minInt = Math.ceil(min);
  const maxInt = Math.floor(max);
  return Math.floor(Math.random() * (maxInt - minInt) + minInt); // The maximum is exclusive and the minimum is inclusive
};

const getSeason = (now: Date) => {
  if (now.getMonth() === 11 && now.getDate() > 18) return "winter";
  if (now.getMonth() === 9 && now.getDate() > 14) return "autumn";
  return null;
};

const numberArray = (size: number) => Array.from(Array(size).keys());

const SnowFlake = () => {
  const [{ randomX, randomScale, fallDuration, fallDelay, opacity }] = useState(
    () => ({
      randomX: getRandomInt(0, 100), // vw
      randomScale: Math.random(),
      fallDuration: getRandomInt(10, 30), // s
      fallDelay: getRandomInt(0, 30) * -1, // s
      opacity: getRandomInt(0, 100),
    }),
  );
  const randomXVw = `${randomX}vw`;
  const randomRotation = `${360 * randomScale}deg`;

  return (
    <div
      className={styles["snow-flake"]}
      style={{
        pointerEvents: "none",
        position: "absolute",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        color: "#c9e7ff",
        opacity: `calc(${opacity} * 0.01)`,
        transform: `translate(${randomXVw}, -10px) scale(${randomScale}) rotate(${randomRotation})`,
        animationDuration: `${fallDuration}s`,
        animationDelay: `${fallDelay}s`,
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
      }}
    >
      <IconFa faIcon={faSnowflake} />
    </div>
  );
};

const SnowFlakes = () => (
  <div className={styles["seasonal-snow-wrapper"]}>
    {numberArray(200).map((item) => (
      <SnowFlake key={`${item}-flake`} />
    ))}
  </div>
);

const brown = "#A52A2A";
const chocolate = "#D2691E";
const goldenRod = "#DAA520";

const autumnColors = [brown, chocolate, goldenRod];

const Leaf = () => {
  const [
    {
      randomX,
      randomScale,
      fallDuration,
      fallDelay,
      colorIndex,
      yRotation,
      driftX,
      endRotation,
    },
  ] = useState(() => ({
    driftX: getRandomInt(-30, 30), // vw
    endRotation: getRandomInt(-720, 720), // deg
    randomX: getRandomInt(10, 90), // vw
    randomScale: Math.random(),
    fallDuration: getRandomInt(7, 20), // s
    fallDelay: getRandomInt(1, 10), // s
    colorIndex: getRandomInt(0, autumnColors.length),
    yRotation: getRandomInt(2, 10),
  }));
  const randomXVw = `${randomX}vw`;
  const randomRotation = `${360 * randomScale}deg`;
  const randomYRotation = `${360 * yRotation}deg`;

  return (
    <div
      className={styles["leaf"]}
      style={{
        pointerEvents: "none",
        perspective: "1000px",
        position: "absolute",
        width: "50px",
        height: "50px",
        borderRadius: "50%",
        color: autumnColors[colorIndex],
        ...({
          "--leaf-start": `translate(${randomXVw}, -100px) scale(${0.5 + randomScale}) rotate(${randomRotation}) rotateY(${randomYRotation})`,
          "--leaf-end": `translate(${randomX + driftX}vw, 100vh) scale(${0.5 + randomScale}) rotate(${endRotation}deg) rotateY(0deg)`,
        } as CSSProperties),
        animationDuration: `${fallDuration}s`,
        animationDelay: `${fallDelay}s`,
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
        animationFillMode: "backwards",
        transformStyle: "preserve-3d",
      }}
    >
      <IconFa faIcon={faCanadianMapleLeaf} />
    </div>
  );
};

const Leaves = () => (
  <div className={styles["seasonal-wrapper"]}>
    {numberArray(40).map((item) => (
      <Leaf key={`${item}-flake`} />
    ))}
    <img className={styles["bat-image"]} src={batGif} alt="" />
  </div>
);

const MemoizedSnowFlakes = memo(SnowFlakes);
const MemoizedLeaves = memo(Leaves);

export const Seasonal = ({ children }: { children: ReactNode }) => {
  // Resolved after mount so server and client markup match (random values, date).
  const [season, setSeason] = useState<ReturnType<typeof getSeason>>(null);

  useEffect(() => {
    setSeason(getSeason(new Date()));
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {season === "winter" && <MemoizedSnowFlakes />}
      {season === "autumn" && <MemoizedLeaves />}
      {children}
    </div>
  );
};

export default Seasonal;
